import type { ThunkDispatch } from 'redux-thunk';
import type { AnyAction } from 'redux';
import commonReducers, { updateCommonData } from '@gumtree/shell/src/reducers/common';
import buyerReducers, { updateBuyerData } from '@gumtree/shell/src/reducers/buyer';
import page, { updatePage } from '@gumtree/shell/src/reducers/page';
import categoryNav from '@gumtree/shell/src/category-nav-ribbon/reducer';
import {
    KeyedReturnTypes,
    ThunkDispatcher,
    RecursivePartial,
} from '@gumtree/shared/src/model/generic.model';

import { hideSearchBar } from '@gumtree/shell/src/header/search-bar/search-bar-actions';
import imageDialog from './image-dialog';
import adDetails, { updateAdDetails } from './ad-details';
import breadcrumbs, { updateBreadcrumbs } from './breadcrumbs';
import images, { updateImages } from './images';
import video, { updateVideo } from './video';
import similarAds, { updateSimilarAds } from './similar-ads';
import mapDetails, { updateMapDetails } from './map-details';
import shareLinks, { updateShareLinks } from './share-links';
import sellerRating, { updateRating } from './seller-rating';
import sellerStats, { updateStats } from './seller-stats';
import sellerContactDetails, { updateContactDetails } from './seller-contact-details';
import sellerActions, { updateSellerActions } from './seller-actions';
import vhcData, { updateVhc } from './vhc';
import { VipData } from '../get-data';
import vipSellerInfo, { updateVipSellerInfo } from './vip-seller-info';
import sellerSkills, { updateSellerSkills } from './seller-skills';
import sellerSkillsList, { updateSellerSkillsList } from './seller-skills-list';
import requestId, { updateRequestId } from './request-id';
import adSource, { updateAdSource } from './ad-source';
import adReviews, { updateAdReviews } from './ad-reviews';
import qaPairs, { updateQaPairs } from './qa-pairs';
import otherServices, { updateOtherServices } from './other-services';
import tabs, { updateVipTabs } from './tabs';
import overview, { updateVipOverview } from './overview';
import postCall from './post-call';

export const showPage =
    ({
        title,
        description,
        adDetails,
        breadcrumbs,
        sellerSkills,
        sellerSkillsList,
        requestId,
        adSource,
        adReviews,
        qaPairs,
        otherServices,
        tabs,
        overview,
        images,
        vhcData,
        video,
        similarAds,
        mapDetails,
        shareLinks,
        sellerRating,
        sellerStats,
        vipSellerInfo,
        sellerContactDetails,
        sellerActions,
        ...data
    }: VipData) =>
    (dispatch: VipUseDispatch) => {
        dispatch(updateVhc(vhcData));
        dispatch(updateAdDetails(adDetails));
        dispatch(updateVipSellerInfo(vipSellerInfo));
        dispatch(updateBreadcrumbs(breadcrumbs));
        dispatch(updateSellerSkills(sellerSkills));
        dispatch(updateSellerSkillsList(sellerSkillsList));
        dispatch(updateRequestId(requestId));
        dispatch(updateAdSource(adSource));
        dispatch(updateAdReviews(adReviews));
        dispatch(updateQaPairs(qaPairs));
        dispatch(updateOtherServices(otherServices));
        dispatch(updateVipTabs(tabs));
        dispatch(updateVipOverview(overview));
        dispatch(updateImages(images));
        dispatch(updateVideo(video));
        dispatch(updateSimilarAds(similarAds));
        dispatch(updateMapDetails(mapDetails));
        dispatch(updateShareLinks(shareLinks));
        dispatch(updateRating(sellerRating));
        dispatch(updateStats(sellerStats));
        dispatch(updateContactDetails(sellerContactDetails));
        dispatch(updateAdReviews(adReviews));
        dispatch(hideSearchBar());

        dispatch(updateCommonData(data as any));
        dispatch(updateBuyerData(data as any));
        dispatch(updatePage({ title, description }));
        dispatch(updateSellerActions(sellerActions));
    };

const defaultReducer = (state = {}) => state;

const index = {
    ...commonReducers,
    ...buyerReducers,
    page,
    imageDialog,
    adDetails,
    breadcrumbs,
    sellerSkills,
    sellerSkillsList,
    requestId,
    adSource,
    adReviews,
    qaPairs,
    otherServices,
    tabs,
    overview,
    sellerContactDetails,
    images,
    video,
    vhcData,
    vipSellerInfo,
    similarAds,
    mapDetails,
    shareLinks,
    sellerRating,
    sellerStats,
    sellerActions,
    categoryNav,
    imageSupport: defaultReducer,
    postCall,
};

export type VipState = KeyedReturnTypes<typeof index>;

export type VipThunk<Payload, Return = any> = ThunkDispatcher<VipState, Payload, Return>;

export type VipUseDispatch = ThunkDispatch<VipState, any, AnyAction>;

export type VipStatePartial = RecursivePartial<VipState>;

export default index;
